import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import { useAppSelector, useAppDispatch } from '../reduxhooks';
import { Button, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, Link, Radio, RadioGroup, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { updateOrderState, getCurrentOrder, getDiscountedPriceAmountWithPromotionsAndShipment, calculateCompleteCartInvoice, resetCartStateAndSetOrderStatus, paymentTokenFetch, removePaymentToken } from '../redux/CartRedux';
import { ESPAY_QRIS_CHANNEL, ensureEspayKitLoaded, espayChannelForMethod, formatRp, kodeUnikForOrder, midtransCodeForMethod, normalizePaymentGroups, terbebasClient } from '../terbebasApi';
import { SetStateAction, useEffect } from 'react';
import OverlayComponentButton, { FareastButton } from './OverlayComponentButton';
import { sendWebsiteEventMessage, sendWebsiteLivestreamMessage } from '../telegrambotevents';
import React from 'react';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import AppsOutlinedIcon from '@material-ui/icons/AppsOutlined';
import { isAnyPaymentMethod, paymentLogoFor } from '../paymentLogos';
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  paymentContent: {
    height: '100%',
    padding: '0px 24px',
  },
  subFormTitle: {
    marginTop: '30px',
    marginBottom: '12px',
    fontSize: "20px",
    fontWeight: 500,
  },
  subFormTitleCategory: {
    marginTop: '32px',
    marginBottom: '10px',
    fontSize: "14px",
    fontWeight: 500,
  },
  paymentPortalButton: {
    marginTop: '20px',
  },
  formNavButtonsContainer: {
    height: 'auto',
    width: '100%',
    right: '0px',
    display: 'flex',
    justifyContent: 'right',
  },
  formNavButtons: {
    marginTop: '20px',
    marginRight: '18px',
    marginBottom: '24px',
  },
  contentText: {
    fontSize: '15px',
    fontWeight: 300,
    marginBottom: '5px',
  },
  priceText: {
    fontFamily: 'Open Sans, sans-serif',
    fontSize: '15px',
    fontWeight: 400,
  },
  paymentOptionBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  chooseOtherPaymentOptionBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "6px",
    marginTop: "8px",
  },
  paymentOptionRadio: {
    color: "#121212 !important",
  },
  paymentOptionImg: {
    // maxHeight: "26px",
    // width: "24px",
    marginRight: "16px",
    width: "32px",
  },
  paymentOptionText: {
    fontSize: '15px',
    fontWeight: 300,
    paddingTop: "3px",
  },
  chooseOtherPaymentMethodText: {
    fontSize: '15px',
    fontWeight: 500,
    marginTop: "14px",
    marginBottom: "10px",
  },
  paymentOptionBcaBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "22px",
    marginBottom: "25px",
  },
  paymentOptionBcaText: {
    fontSize: '14px',
    fontWeight: 400,
    paddingTop: "3px",
  },
  contentPaymentSmallText: {
    fontSize: '15px',
    fontWeight: 300,
    marginBottom: "2px"
  },
  contentPaymentText: {
    fontSize: "24px",
    fontWeight: 600,
    marginBottom: "18px"
  },
  salinBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  salinButton: {
    height: "35px",
    marginTop: "-5px",
  },
  snapWindow: {
    width: '100%'
  },
  circularProgressContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "250px",
  },
}),
);

interface PageCheckoutProps {
  checkoutStep: string,
  setCheckoutStep: React.Dispatch<SetStateAction<string>>,
}

export default function PageCheckoutForms({checkoutStep, setCheckoutStep} : PageCheckoutProps) {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  // choosePaymentMethod | commitManual | commitQrisStatic | commitAutomated | commitEspay
  const [paymentMethodStep, setPaymentMethodStep] = React.useState<string>("choosePaymentMethod");
  const [claimBusy, setClaimBusy] = React.useState(false);
  const [claimError, setClaimError] = React.useState('');
  // ESPAY Embed Kit session ({apiKey, paymentId, isProduction} from the
  // backend); local state like the claim flow — nothing else consumes it.
  const [espayData, setEspayData] = React.useState<{ apiKey: string; paymentId: string; isProduction: boolean } | null>(null);
  const [espayError, setEspayError] = React.useState('');
  const totalPayment = useAppSelector(state => getDiscountedPriceAmountWithPromotionsAndShipment(state.cart, state.productsList, state.userMeasurementsList.userBodyMeasurementsDictionary, state.userMeasurementsList.userClothingMeasurementsDictionary));
  const paymentStep = useAppSelector(state => state.cart.orderStatus);
  const orderId = useAppSelector(state => (state.cart.orderData as any)?.orderId ?? (state.cart.orderData as any)?.id ?? '');
  const paymentToken = useAppSelector(state => state.cart.paymentToken);
  // Server-authoritative order figures (the raw order row lands in redux at
  // checkout; cleared once the order completes, so read while waiting for
  // payment). The displayed manual/QRIS amount is an exact-match
  // verification key, so prefer the server total over the client-side cart
  // recompute, which is a second implementation of the pricing rules.
  const orderNumber = useAppSelector(state => Number((state.cart.orderData as any)?.orderNumber ?? 0));
  const serverTotal = useAppSelector(state => {
    const t = (state.cart.orderData as any)?.total;
    return t !== undefined && t !== null ? Number(t) : undefined;
  });
  const baseTotal = serverTotal ?? totalPayment;
  // Amount to pay for manual/QRIS Statis: total + 3-digit kode unik.
  const payableTotal = baseTotal + kodeUnikForOrder(orderNumber);
  // Per-store WhatsApp destination for the "Saya Sudah Bayar" hand-off.
  const storeWhatsapp = useAppSelector(state =>
    ((state.storeProfile.profile?.config?.contact as any)?.whatsapp ?? '') as string);
  const qrisStaticImageUrl = useAppSelector(state =>
    ((state.storeProfile.profile?.config as any)?.qrisStaticImageUrl ?? '') as string);

  // Merchant-configured payment groups (ordered), each with methods that are
  // fulfilled manually (transfer + confirm), automated (Midtrans), via QRIS
  // (Midtrans other_qris), or with the store's static QRIS image.
  const paymentGroupsRaw = useAppSelector(state => state.storeProfile.profile?.config?.payments);
  // qris_static methods are hidden until the merchant uploads the store's
  // QRIS image — otherwise the buyer would hit a dead end.
  const paymentGroups = normalizePaymentGroups(paymentGroupsRaw)
    .map((g) => ({
      ...g,
      supportedMethods: (g.supportedMethods ?? []).filter(
        (m) => m.mode !== 'qris_static' || !!qrisStaticImageUrl),
    }))
    .filter((g) => g.supportedMethods.length > 0);
  const flatMethods = paymentGroups.flatMap((g) =>
    (g.supportedMethods ?? []).map((m, mi) => ({ groupLabel: g.label, method: m, key: g.label + ':' + mi })));
  // Default to QRIS when nothing is configured (keeps stores working).
  const effectiveMethods = flatMethods.length ? flatMethods
    : [{ groupLabel: 'QRIS', method: { method: 'QRIS', mode: 'qris' as const }, key: 'default-qris' }];
  const [selectedKey, setSelectedKey] = React.useState<string>(effectiveMethods[0].key);
  const selected = effectiveMethods.find((m) => m.key === selectedKey) ?? effectiveMethods[0];
  const selectedMode = selected.method.mode;
  const useEspay = selectedMode === 'espay' || selectedMode === 'espay_qris';
  const useMidtrans = selectedMode !== 'manual' && selectedMode !== 'qris_static' && !useEspay;
  // StoreConfig.paymentAutoSelectSingle: with exactly one configured method
  // the chooser is pointless, so the buyer lands straight on that method's
  // payment flow (e.g. the ESPAY hosted page) and the "Pilih Metode
  // Pembayaran Lain" back links are hidden. Counts configured methods only —
  // the built-in QRIS fallback for unconfigured stores is not a merchant
  // choice. Fires once per mount: Snap's onClose/onPending return to the
  // chooser, which then shows the single option + PAY NOW instead of
  // re-opening Snap in a loop.
  const paymentAutoSelectSingle = useAppSelector(state =>
    (state.storeProfile.profile?.config as any)?.paymentAutoSelectSingle === true);
  const autoSelected = paymentAutoSelectSingle && flatMethods.length === 1;
  const autoFiredRef = React.useRef(false);

  useEffect(() => {
    if (paymentToken !== "") {
      (window as any).snap.hide();

      console.log("Setting snap to paymentToken " + paymentToken);
      (window as any).snap.embed(paymentToken, {
        embedId: 'snap-container',
        selectedPaymentType: "",
        language: "id",
        onSuccess: function(result: any){
          console.log("success: ",result);

          sendWebsiteLivestreamMessage("[CHECKOUT] Payment total of Rp " + totalPayment + " is successful! Congratulations!");
          // Facebook Purchase Event, Based on Midtrans
          // if ((window as any).fbq !== null) { 
          //   (window as any).fbq('track', 'Purchase', {
          //     currency: "IDR",
          //     value: totalPayment,
          //   })
          // }

          dispatch(updateOrderState('verifyPaymentWithServer'));
          dispatch(getCurrentOrder());
        },
        onPending: function(result: any){
          console.log("pending: ",result);

          (window as any).snap.hide();
          setPaymentMethodStep("choosePaymentMethod");
        },
        onError: function(result: any){
          console.log("error: ",result);
        },
        onClose: function(){
          console.log("close: ");

          (window as any).snap.hide();
          setPaymentMethodStep("choosePaymentMethod");
        }});
    }
  }, [paymentToken]);

  // ESPAY Embed Kit: once the session is fetched and the commitEspay branch
  // (with #sgoplus-iframe) is mounted, load the kit script and let it
  // populate the iframe. The amount never travels through the embed — ESPAY
  // fetches it from the backend's inquiry callback.
  useEffect(() => {
    if (!espayData || paymentMethodStep !== 'commitEspay') return;
    ensureEspayKitLoaded(espayData.isProduction)
      .then(() => {
        const sgo = (window as any).SGOSignature;
        // getIframeURL only BUILDS the hosted-page URL (signature + domain +
        // params) — assigning it to the iframe is on us. The kit appends
        // every key of this object to the URL, so bankCode/bankProduct ride
        // along as ESPAY's AutoDirect params: the hosted page then opens the
        // chosen channel (e.g. QRIS Plus dynamic QR) instead of its chooser,
        // mirroring Midtrans enabled_payments. Unmapped names (Lainnya, …)
        // send neither and get the chooser.
        const channel = selectedMode === 'espay_qris'
          ? ESPAY_QRIS_CHANNEL
          : espayChannelForMethod(selected.method.method);
        const url = sgo.getIframeURL({
          key: espayData.apiKey,
          paymentId: espayData.paymentId,
          backUrl: window.location.origin + '/order/' + orderId,
          ...(channel ? { bankCode: channel.bankCode, bankProduct: channel.bankProduct } : {}),
        });
        const iframe = document.getElementById('sgoplus-iframe') as HTMLIFrameElement | null;
        if (iframe) iframe.src = url;
        sgo.receiveForm();
      })
      .catch((e) => setEspayError(e?.message ?? 'Gagal memuat pembayaran'));
  }, [espayData, paymentMethodStep]);

  // The Embed Kit has no onSuccess hook (payment settles via the backend
  // webhook), so poll the order while the ESPAY step is open and advance the
  // same way Snap's onSuccess does.
  useEffect(() => {
    if (paymentMethodStep !== 'commitEspay') return;
    const interval = setInterval(async () => {
      try {
        const res = await terbebasClient.get('/storefront/orders/' + orderId);
        if (res.data?.financialStatus === 'PAID') {
          sendWebsiteLivestreamMessage("[CHECKOUT] Pembayaran ESPAY sebesar Rp " + baseTotal + " berhasil!");
          dispatch(updateOrderState('verifyPaymentWithServer'));
          dispatch(getCurrentOrder());
        }
      } catch (e) { /* transient poll failure — next tick retries */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [paymentMethodStep, orderId]);

  // PAY NOW: manual → account + confirm; qris_static → QRIS image + confirm;
  // espay/espay_qris → ESPAY hosted checkout iframe (channel pinned via
  // AutoDirect); automated/qris → Midtrans Snap restricted to the chosen
  // channel.
  const handlePayNow = () => {
    sendWebsiteLivestreamMessage("[CHECKOUT] Payment method " + selected.groupLabel + "/" + selected.method.method + " (" + selectedMode + ") dipilih.");
    if (selectedMode === 'manual') {
      setPaymentMethodStep('commitManual');
      return;
    }
    if (selectedMode === 'qris_static') {
      setPaymentMethodStep('commitQrisStatic');
      return;
    }
    if (useEspay) {
      setEspayData(null); setEspayError('');
      terbebasClient.post('/storefront/orders/' + orderId + '/espay-pay', {})
        .then((res) => setEspayData(res.data))
        .catch((e) => setEspayError(e?.response?.data?.error ?? 'Gagal membuat pembayaran'));
      setPaymentMethodStep('commitEspay');
      return;
    }
    dispatch(removePaymentToken());
    // "Lainnya" has no channel code on purpose: enabledPayments stays
    // undefined and Snap opens with every channel the merchant enabled.
    const code = midtransCodeForMethod(selected.method.method);
    const enabledPayments = selectedMode === 'qris'
      ? ['other_qris']
      : (code ? [code] : undefined);
    dispatch(paymentTokenFetch({ orderId, enabledPayments }));
    setPaymentMethodStep('commitAutomated');
  };

  useEffect(() => {
    if (!autoSelected || autoFiredRef.current) return;
    if (paymentStep !== 'waitingForPayment' || paymentMethodStep !== 'choosePaymentMethod' || !orderId) return;
    autoFiredRef.current = true;
    handlePayNow();
  }, [autoSelected, paymentStep, paymentMethodStep, orderId]);

  // Back-to-chooser link shared by every commit step; hidden when the single
  // method was auto-selected (nothing else to pick), except after an ESPAY
  // session error so the buyer can retry via PAY NOW.
  const chooseOtherPaymentLink = (
    <Link onClick={() => {
      (window as any).snap?.hide?.();
      setEspayData(null); setEspayError('');
      setPaymentMethodStep("choosePaymentMethod");
    }}>
      <Box className={classes.chooseOtherPaymentOptionBox} >
        <ArrowBackOutlinedIcon className={classes.paymentOptionImg} />
        <Typography className={classes.chooseOtherPaymentMethodText}>
          Pilih Metode Pembayaran Lain
        </Typography>
      </Box>
    </Link>
  );

  // "Saya Sudah Bayar": records the claim on the order (merchant gets an
  // alert and reconciles the kode-unik amount on their statement), then
  // hands the buyer off to the store's WhatsApp with the payment details.
  const submitPaymentClaim = async (provider: 'manual-transfer' | 'qris-static') => {
    if (claimBusy) return;
    setClaimBusy(true); setClaimError('');
    try {
      await terbebasClient.post('/storefront/orders/' + orderId + '/payment-claim', { provider });
      // wa.me needs the international form; merchants often store 08xx.
      const waNumber = storeWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62');
      if (waNumber) {
        const waText = 'Permisi kak, saya baru bayar sebesar ' + formatRp(payableTotal)
          + ' via ' + selected.method.method
          + ' untuk pembayaran order #' + (orderNumber || orderId);
        window.open('https://wa.me/' + waNumber + '?text=' + encodeURIComponent(waText), '_blank');
      }
      sendWebsiteLivestreamMessage("[CHECKOUT] Konfirmasi bayar (" + provider + ") sebesar " + formatRp(payableTotal) + " untuk order " + orderId);
      dispatch(resetCartStateAndSetOrderStatus("success"));
    } catch (e: any) {
      setClaimError(e?.response?.data?.error ?? 'Gagal menyimpan konfirmasi pembayaran');
    } finally { setClaimBusy(false); }
  };

  return (
      <div className={classes.paymentContent}>
        {
          paymentStep === "waitingForPayment" ?
          (paymentMethodStep === "choosePaymentMethod" ?
          <>
            <Typography variant="h5" className={classes.subFormTitle}>
                Metode Pembayaran
            </Typography>
            <Typography className={classes.contentText}>
                Total yang harus dibayar sebesar <span className={classes.priceText}>{formatRp(baseTotal)}.</span>
            </Typography>
            <FormControl component="fieldset" style={{ width: '100%' }}>
              <RadioGroup aria-label="paymethod" name="paymethod1" value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}>
                {
                  paymentGroups.map((group) => (
                    <React.Fragment key={group.label}>
                      <Typography variant="h5" className={classes.subFormTitleCategory}>
                        {group.label}
                      </Typography>
                      {
                        (group.supportedMethods ?? []).map((m, mi) => {
                          const key = group.label + ':' + mi;
                          const logo = paymentLogoFor(m.method);
                          return (
                            <FormControlLabel key={key} value={key}
                              control={<Radio className={classes.paymentOptionRadio} />}
                              label={
                                <Box className={classes.paymentOptionBox}>
                                  {logo
                                    ? <img className={classes.paymentOptionImg} src={logo} alt={m.method} />
                                    : isAnyPaymentMethod(m.method)
                                    ? <AppsOutlinedIcon className={classes.paymentOptionImg} />
                                    : <AccountBalanceWalletOutlinedIcon className={classes.paymentOptionImg} />}
                                  <Typography className={classes.paymentOptionText}>
                                    {m.method}
                                    {/* {m.mode === 'manual' ? ' (transfer manual)' : m.mode === 'qris' ? ' (via QRIS)' : ''} */}
                                  </Typography>
                                </Box>
                              } />
                          );
                        })
                      }
                    </React.Fragment>
                  ))
                }
                {
                  paymentGroups.length === 0 &&
                  <>
                    <Typography variant="h5" className={classes.subFormTitleCategory}>QRIS</Typography>
                    <FormControlLabel value="default-qris"
                      control={<Radio className={classes.paymentOptionRadio} />}
                      label={
                        <Box className={classes.paymentOptionBox}>
                          <img className={classes.paymentOptionImg} src={paymentLogoFor('QRIS')!} alt="QRIS" />
                          <Typography className={classes.paymentOptionText}>QRIS</Typography>
                        </Box>
                      } />
                  </>
                }
              </RadioGroup>
            </FormControl>
            <OverlayComponentButton handleClick={handlePayNow}
              formButtonText="PAY NOW"
              isTypeSubmit={false}
              buttonMarginTopBottom="20px"
              buttonMarginLeftRight="10px"/>
          </> :
          paymentMethodStep === "commitManual" ?
          <>
            {(!autoSelected || !!espayError) && chooseOtherPaymentLink}
            <Box>
              <Typography variant="h5" className={classes.subFormTitle}>
                  {selected.groupLabel} — {selected.method.method}
              </Typography>
              <Typography className={classes.contentPaymentSmallText}>
                Transfer ke tujuan berikut, lalu tekan "Saya Sudah Bayar".
                Pesanan diproses setelah pembayaran kami verifikasi.
              </Typography>
              {
                selected.method.accountNumber ?
                <Box style={{ margin: '14px 0px' }}>
                  <Typography className={classes.contentPaymentSmallText}>
                    {selected.method.method}{selected.method.accountName ? ' — a.n. ' + selected.method.accountName : ''}
                  </Typography>
                  <Box className={classes.salinBox}>
                    <Typography className={classes.contentPaymentText}>
                      {selected.method.accountNumber}
                    </Typography>
                    <Button variant="outlined" className={classes.salinButton}
                      startIcon={<FileCopyOutlinedIcon />} size="small"
                      onClick={() => { (navigator as any).clipboard.writeText(selected.method.accountNumber); }}>
                      Salin
                    </Button>
                  </Box>
                </Box> : null
              }
              <Typography className={classes.contentPaymentSmallText}>
                Jumlah yang Harus Ditransfer
              </Typography>
              <Typography className={classes.contentPaymentText}>
                {formatRp(payableTotal)}
              </Typography>
              <Typography className={classes.contentPaymentSmallText}>
                Sudah termasuk kode unik {kodeUnikForOrder(orderNumber)} — transfer tepat sampai 3 digit terakhir agar pembayaran cepat kami kenali.
              </Typography>
              {
                claimError ?
                <Typography style={{ color: 'red', fontSize: '12px', marginTop: '6px' }}>{claimError}</Typography> : null
              }
              <OverlayComponentButton handleClick={() => submitPaymentClaim('manual-transfer')}
                formButtonText={claimBusy ? "MENGIRIM..." : "SAYA SUDAH BAYAR"}
                isTypeSubmit={false}
                buttonMarginTopBottom="20px"
                buttonMarginLeftRight="10px"/>
            </Box>
          </> :
          paymentMethodStep === "commitQrisStatic" ?
          <>
            {(!autoSelected || !!espayError) && chooseOtherPaymentLink}
            <Box>
              <Typography variant="h5" className={classes.subFormTitle}>
                  {selected.groupLabel} — {selected.method.method}
              </Typography>
              <Typography className={classes.contentPaymentSmallText}>
                Scan QRIS di bawah, bayar sejumlah tertera, lalu tekan "Saya Sudah Bayar".
                Pesanan diproses setelah pembayaran kami verifikasi.
              </Typography>
              <Box style={{ margin: '14px 0px' }}>
                <img src={qrisStaticImageUrl} alt="QRIS"
                  style={{ maxWidth: '260px', width: '100%', display: 'block', border: '1px solid #e0e0e0', borderRadius: '6px' }} />
              </Box>
              {
                selected.method.accountName ?
                <>
                  <Typography className={classes.contentPaymentSmallText}>
                    Nama Penerima
                  </Typography>
                  <Typography className={classes.contentPaymentText}>
                    {selected.method.accountName}
                  </Typography>
                </> : null
              }
              <Typography className={classes.contentPaymentSmallText}>
                Jumlah yang Harus Dibayar
              </Typography>
              <Typography className={classes.contentPaymentText}>
                {formatRp(payableTotal)}
              </Typography>
              <Typography className={classes.contentPaymentSmallText}>
                Sudah termasuk kode unik {kodeUnikForOrder(orderNumber)} — bayar tepat sampai 3 digit terakhir agar pembayaran cepat kami kenali.
              </Typography>
              {
                claimError ?
                <Typography style={{ color: 'red', fontSize: '12px', marginTop: '6px' }}>{claimError}</Typography> : null
              }
              <OverlayComponentButton handleClick={() => submitPaymentClaim('qris-static')}
                formButtonText={claimBusy ? "MENGIRIM..." : "SAYA SUDAH BAYAR"}
                isTypeSubmit={false}
                buttonMarginTopBottom="20px"
                buttonMarginLeftRight="10px"/>
            </Box>
          </> :
          paymentMethodStep === "commitEspay" ?
          <>
            {(!autoSelected || !!espayError) && chooseOtherPaymentLink}
            {
              espayError ?
              <Typography style={{ color: 'red', fontSize: '12px', marginTop: '6px' }}>{espayError}</Typography> :
              !espayData ?
              <Box className={classes.circularProgressContainer}>
                <CircularProgress/>
              </Box> : null
            }
            {/* Populated by SGOSignature.getIframeURL once the kit loads. */}
            <iframe id="sgoplus-iframe" src="" title="ESPAY"
              style={{ width: '100%', minHeight: '480px', border: 0, display: espayData && !espayError ? 'block' : 'none' }} />
          </> :
          <>
            {(!autoSelected || !!espayError) && chooseOtherPaymentLink}
          </>) :
          paymentStep === 'verifyPaymentWithServer' ?
          <Typography className={classes.contentText}>
            Please wait while we verify your payment with the server...
          </Typography> :
          null
        }
        {
          paymentStep === "waitingForPayment" ?
          <div id="snap-container" className={classes.snapWindow}></div> :
          null
        }
        {
          paymentStep === "waitingForPayment"
          && paymentMethodStep === "commitAutomated"
          && useMidtrans
          && paymentToken === ""
          ?
          <Box className={classes.circularProgressContainer}>
            <CircularProgress/>
          </Box>:
          null
        }
      </div>
  );
}